import { cx } from '@linaria/core';
import { useRef, useState, useCallback, useEffect } from 'react';
import { useUniqueId } from '@i-ark/hooks';
import { Dropdown } from '@ui/kit';
import { Color } from './Color.js';
import { togglerCls, COLORS, popupCls } from './ColorPicker.style.js';
import { Transparent } from './Transparent.js';
import { jsxs, jsx } from 'react/jsx-runtime';

const RESET_PREVIEW_AFTER_MS = 100;
function ColorPicker({
  placement,
  style,
  className,
  children,
  allowTransparency,
  value,
  onPreview,
  onPreviewReset,
  onChange,
  onOpen,
  onClose,
  ...props
}) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const togglerRef = props.togglerRef ?? useRef(null);
  const popupRef = useRef(null);
  const id = `colorpicker-${useUniqueId().toString()}`;
  const [previewValue, setPreviewValue] = useState(undefined);
  const [showPopMenu, setShowPopMenu] = useState(false);
  const prevColorRef = useRef(value);
  const showPopMenuRef = useRef(showPopMenu);
  const previewTimeout = useRef(undefined);
  const handleClose = useCallback(evt => {
    evt.stopPropagation();
    evt.preventDefault();
    window.clearTimeout(previewTimeout.current);
    if (onClose !== undefined) {
      onClose(prevColorRef.current);
    }
    setShowPopMenu(false);
    setPreviewValue(undefined);
    showPopMenuRef.current = false;
    if (togglerRef.current !== null) {
      togglerRef.current.focus({
        preventScroll: true
      });
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [onClose]);
  const handleTogglerClick = useCallback(() => {
    if (!showPopMenu) {
      if (onOpen !== undefined) {
        onOpen();
        prevColorRef.current = value;
      }
    } else {
      if (onClose !== undefined) {
        onClose(prevColorRef.current);
      }
    }
    setShowPopMenu(!showPopMenu);
    showPopMenuRef.current = !showPopMenu;
    if (togglerRef.current !== null) {
      togglerRef.current.focus({
        preventScroll: true
      });
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [onOpen, onClose, showPopMenu, value]);
  const handlePreview = useCallback(tmpValue => {
    if (!showPopMenuRef.current) {
      return;
    }
    window.clearTimeout(previewTimeout.current);
    if (onPreview !== undefined) {
      onPreview(tmpValue);
    }
    setPreviewValue(tmpValue);
  }, [onPreview]);
  const handlePreviewReset = useCallback(() => {
    if (!showPopMenuRef.current) {
      return;
    }
    window.clearTimeout(previewTimeout.current);
    previewTimeout.current = window.setTimeout(() => {
      if (onPreviewReset !== undefined) {
        onPreviewReset(prevColorRef.current);
      }
      setPreviewValue(undefined);
    }, RESET_PREVIEW_AFTER_MS);
  }, [onPreviewReset]);
  const handleColorClick = useCallback(nextValue => {
    window.clearTimeout(previewTimeout.current);
    if (onChange !== undefined) {
      onChange(nextValue);
    }
    if (onClose !== undefined) {
      onClose(nextValue);
    }
    setPreviewValue(undefined);
    setShowPopMenu(false);
    showPopMenuRef.current = false;
    if (togglerRef.current !== null) {
      togglerRef.current.focus({
        preventScroll: true
      });
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [onChange, onClose]);
  useFocusInsideOnExpand({
    showPopMenu,
    popupRef,
    togglerRef
  });
  useKeyboardNavigation({
    showPopMenu,
    popupRef
  });
  const listenerProps = {
    onClick: handleColorClick,
    onPreview: handlePreview,
    onPreviewReset: handlePreviewReset
  };
  return /*#__PURE__*/jsxs("label", {
    ref: props.ref,
    style: style,
    children: [/*#__PURE__*/jsx("input", {
      name: id,
      type: "hidden",
      readOnly: true
    }), /*#__PURE__*/jsx("button", {
      ref: togglerRef,
      className: cx(togglerCls, className, showPopMenu && "togler--active", children !== undefined && "toggler--hasIcon"),
      style: {
        backgroundColor: previewValue ?? value
      },
      onClick: handleTogglerClick,
      "aria-haspopup": "true",
      "aria-expanded": showPopMenu,
      "aria-controls": id,
      children: children
    }), /*#__PURE__*/jsx(Dropdown, {
      disableFocusOn: true,
      triggerRef: togglerRef,
      placement: placement ?? "bottom",
      active: showPopMenu,
      onClose: handleClose,
      offsetPx: 20,
      children: /*#__PURE__*/jsxs("ul", {
        ref: popupRef,
        id: id,
        className: popupCls,
        role: "listbox",
        children: [allowTransparency === true && /*#__PURE__*/jsx(Transparent, {
          selected: value === "transparent",
          ...listenerProps
        }), COLORS.map(color => typeof color === "string" ? /*#__PURE__*/jsx(Color, {
          allowTransparency: allowTransparency === true,
          color: color,
          selected: color === value,
          ...listenerProps
        }, color) : /*#__PURE__*/jsx(Color, {
          allowTransparency: allowTransparency === true,
          color: color[0] ?? COLORS[0],
          borderColor: color[1],
          selected: color[0] === value,
          ...listenerProps
        }, color[0]))]
      })
    })]
  });
}
function useFocusInsideOnExpand({
  showPopMenu,
  popupRef,
  togglerRef
}) {
  const wasMounted = useRef(false);
  useEffect(() => {
    // don't steal focus when initially mounting the node
    if (!wasMounted.current) {
      wasMounted.current = true;
      return;
    }
    if (showPopMenu) {
      if (popupRef.current !== null) {
        // auto-focus currently selected or first available item when the
        // dropdown is expanded
        const selectedItem = popupRef.current.querySelector('[aria-selected="true"]');
        if (selectedItem !== null) {
          selectedItem.focus({
            preventScroll: true
          });
        }
      }
    } else {
      if (popupRef.current !== null && togglerRef.current !== null) {
        if (popupRef.current.contains(document.querySelector(":focus"))) {
          togglerRef.current.focus({
            preventScroll: true
          });
        }
      }
    }
  }, [showPopMenu, popupRef, togglerRef]);
}
function useKeyboardNavigation({
  showPopMenu,
  popupRef
}) {
  useEffect(() => {
    const container = popupRef.current;
    if (!showPopMenu || container === null) {
      return;
    }
    const onKeyPress = evt => {
      const currentlyFocused = container.querySelector(":focus")?.parentElement?.parentElement;
      if (!currentlyFocused) {
        container.querySelector(":first-child button")?.focus();
        return;
      }
      switch (evt.key) {
        case "ArrowRight":
          if (currentlyFocused.nextElementSibling !== null) {
            currentlyFocused.nextElementSibling.querySelector("button").focus();
          }
          evt.preventDefault();
          break;
        case "ArrowLeft":
          if (currentlyFocused.previousElementSibling !== null) {
            currentlyFocused.previousElementSibling.querySelector("button").focus();
          }
          evt.preventDefault();
          break;
        case "ArrowDown":
          {
            evt.preventDefault();
            if (currentlyFocused.matches("[data-transparent]")) {
              if (currentlyFocused.nextElementSibling !== null) {
                currentlyFocused.nextElementSibling.querySelector("button").focus();
              }
              return;
            }
            const nextDesiredItem = currentlyFocused?.nextElementSibling?.nextElementSibling?.nextElementSibling?.nextElementSibling?.nextElementSibling;
            if (nextDesiredItem != null) {
              nextDesiredItem.querySelector("button").focus();
            }
            break;
          }
        case "ArrowUp":
          {
            evt.preventDefault();
            const nextDesiredItem = currentlyFocused?.previousElementSibling?.previousElementSibling?.previousElementSibling?.previousElementSibling?.previousElementSibling ?? container.querySelector("[data-transparent]");
            if (nextDesiredItem !== null) {
              nextDesiredItem.querySelector("button").focus();
            }
            break;
          }
      }
    };
    window.addEventListener("keydown", onKeyPress);
    return () => {
      window.removeEventListener("keydown", onKeyPress);
    };
  }, [showPopMenu, popupRef]);
}

export { ColorPicker };
//# sourceMappingURL=ColorPicker.js.map
